"use client";

import Link from "next/link";
import { useSession } from "next-auth/react";
import { useMemo } from "react";
import {
  Building2,
  ExternalLink,
  MapPinned,
  Scale,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import GondolaLogo from "@/components/ui/svgComponents/GondolaLogo";

interface WelcomeScreenProps {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
}

interface ConciergeService {
  title: string;
  description: string;
  prompt: string;
  icon: LucideIcon;
}

function futureWeekend(referenceDate: Date, weeksAhead: number) {
  const start = new Date(
    Date.UTC(
      referenceDate.getUTCFullYear(),
      referenceDate.getUTCMonth(),
      referenceDate.getUTCDate()
    )
  );
  const daysUntilFriday = (5 - start.getUTCDay() + 7) % 7 || 7;
  start.setUTCDate(start.getUTCDate() + daysUntilFriday + weeksAhead * 7);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 2);
  return { start, end };
}

function formatDateRange(start: Date, end: Date, monthStyle: "long" | "short") {
  const month = (date: Date) =>
    date.toLocaleString("en-US", { month: monthStyle, timeZone: "UTC" });
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const separator = monthStyle === "long" ? " to " : "-";

  if (sameMonth && sameYear) {
    return `${month(start)} ${start.getUTCDate()}${separator}${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  if (sameYear) {
    return `${month(start)} ${start.getUTCDate()}${separator}${month(end)} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }
  return `${month(start)} ${start.getUTCDate()}, ${start.getUTCFullYear()}${separator}${month(end)} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
}

export function buildConciergeServices(
  referenceDate = new Date()
): ConciergeService[] {
  const planningDates = futureWeekend(referenceDate, 6);
  const searchDates = futureWeekend(referenceDate, 8);
  const comparisonDates = futureWeekend(referenceDate, 10);

  return [
    {
      title: "Plan something new",
      description: "Shape the destination, stay, and details around your trip.",
      prompt: `I'm looking for a true two bedroom suite in Las Vegas for the weekend of ${formatDateRange(planningDates.start, planningDates.end, "short")}. What are my best options?`,
      icon: MapPinned,
    },
    {
      title: "Find the right stay",
      description:
        "Search live rates with your status and preferences in mind.",
      prompt: `Find me a hotel in Los Angeles for ${formatDateRange(searchDates.start, searchDates.end, "long")} that I'd actually like based on where I’ve stayed in the past. Pull my Member rates, compare cash vs points (and whether it’s actually a good redemption value), and factor in my balances, status, and transfer partners.`,
      icon: Building2,
    },
    {
      title: "Compare Options",
      description: "Weigh cash, points, perks, and flexibility side by side.",
      prompt: `At Park Hyatt New York for ${formatDateRange(comparisonDates.start, comparisonDates.end, "short")}, show me every suite I could book, what each costs in cash and points, and whether the suite is worth the points premium over a standard room.`,
      icon: Scale,
    },
  ];
}
export const DEFAULT_PROMPTS = new Set([
  "Tell me what you can help me plan as a traveler",
  "Help me choose a destination for a weekend trip",
  "Find the best hotel for a trip I'm planning",
  "Show me how to compare hotel options and rates",
]);

export default function WelcomeScreen({
  suggestions,
  onSuggestionClick,
}: WelcomeScreenProps) {
  const session = useSession()?.data;
  const firstName = session?.user?.name?.trim().split(/\s+/)[0];
  const conciergeServices = useMemo(() => buildConciergeServices(), []);
  const personalSuggestions = suggestions
    .filter((prompt) => !DEFAULT_PROMPTS.has(prompt))
    .slice(0, 2);

  return (
    <div className="flex min-h-full flex-col justify-center py-4 md:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center">
        <div className="mb-5 flex size-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10 md:mb-7 md:border-brand-gold/30 md:bg-brand-gold/10 md:shadow-sm">
          <GondolaLogo
            aria-hidden="true"
            focusable="false"
            color="currentColor"
            className="size-8 text-primary md:text-brand-gold"
          />
        </div>

        <p className="mb-2 text-xs font-medium uppercase tracking-widest text-primary md:mb-3 md:text-brand-gold">
          Gondola Concierge
        </p>
        <h2 className="text-center text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          {firstName
            ? `Welcome back, ${firstName}.`
            : "Where should we go next?"}
        </h2>
        <p className="mt-3 max-w-xl text-center text-sm leading-6 text-muted-foreground md:mt-4 md:text-base md:leading-7">
          Bring your hardest travel questions. Concierge does the deep research,
          weighs the tradeoffs, and finds the smartest way to pull it off given
          your travel profile.
        </p>
        <div className="mt-10 hidden w-full gap-3 md:grid md:grid-cols-3">
          {conciergeServices.map(
            ({ title, description, prompt, icon: Icon }) => (
              <Button
                key={title}
                variant="outline"
                size="none"
                aria-label={title}
                onClick={() => onSuggestionClick(prompt)}
                className="group h-auto min-h-48 flex-col items-start justify-between rounded-2xl border-border bg-card p-5 text-left shadow-sm hover:-translate-y-0.5 hover:border-brand-gold/50 hover:bg-card hover:shadow-md"
              >
                <span className="flex size-9 items-center justify-center rounded-lg bg-muted text-foreground transition-colors group-hover:bg-brand-gold/15 group-hover:text-brand-gold">
                  <Icon aria-hidden="true" className="size-4" />
                </span>
                <span className="mt-5 block w-full">
                  <span className="block min-w-0">
                    <span className="block truncate text-sm font-semibold leading-6 text-foreground md:text-base md:leading-7">
                      {title}
                    </span>
                    <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground md:min-h-20 md:text-sm md:leading-6">
                      {description}
                    </span>
                  </span>
                </span>
              </Button>
            )
          )}
        </div>

        {personalSuggestions.length > 0 ? (
          <div className="mt-7 hidden flex-wrap items-center justify-center gap-2 md:flex">
            <span className="mr-1 text-xs text-muted-foreground md:text-sm">
              Or start here
            </span>
            {personalSuggestions.map((suggestion) => (
              <Button
                key={suggestion}
                variant="ghost"
                size="sm"
                onClick={() => onSuggestionClick(suggestion)}
                className="h-auto rounded-full border border-border bg-background px-3 py-2 text-xs font-normal text-foreground hover:border-brand-gold/40 hover:bg-muted md:text-sm"
              >
                {suggestion}
              </Button>
            ))}
          </div>
        ) : null}

        <p className="mt-9 hidden text-center text-xs text-muted-foreground md:block md:text-sm">
          Prefer your own AI client?{" "}
          <Link
            href="/mcp"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-foreground transition-colors hover:text-brand-gold"
          >
            Connect Gondola MCP
            <ExternalLink aria-hidden="true" className="size-3" />
          </Link>
        </p>
      </div>
    </div>
  );
}
